"use client";

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { Drawer } from "vaul";
import {
  useWalletInfoContext,
  type WalletInfoView,
} from "../providers/wallet-info-provider";
import BigNumber from "bignumber.js";
import { useTokenBalance } from "../queries/use-token-balance";
import {
  useForgeHoldings,
  useForgeTokenImages,
} from "../queries/use-forge-holdings";
import {
  CHAIN_IDS,
  getChainBadgeUrl,
  getPortraitUrl,
  isCrossdTokenAddress,
} from "../constants";
import { useTokenStats, buildStatsKey } from "../queries/use-token-stats";
import {
  formatCrossdAprPercent,
  useCrossdApr,
} from "../queries/use-crossd-apr";
import { useTokenInfo, buildCategoryKey } from "../queries/use-token-info";
import { useTotalValue } from "../queries/use-total-value";
import type { Environment } from "../../../types";
import type { ChainId, TokenBalance } from "../types";
import type { BridgeToken } from "../../bridge";
import { QRCodeSVG } from "qrcode.react";
import {
  CloseIcon,
  CopyIcon,
  CheckIcon,
  QRIcon,
  QRLogoIcon,
  BackIcon,
  ChevronDownIcon,
  EyeOpenIcon,
  EyeClosedIcon,
  DisconnectIcon,
  BuyActionIcon,
  BridgeActionIcon,
  SendActionIcon,
  PortfolioActionIcon,
  ToastCheckIcon,
} from "./icons";
import {
  shortenAddress,
  formatWei,
  formatPrice,
  formatTokenValue,
} from "../../../lib/format";
import { cn } from "../../../lib/utils";
import { handleOutlinkClick } from "../../../lib/outlink";
import {
  resolveEnvValue,
  useRemoteConfigValue,
  type AppsConfig,
} from "../../../lib/remote-config";
import { WalletPortfolioBody } from "../../wallet-portfolio";
import type {
  SendFailedInfo,
  SendSubmittedInfo,
} from "../../send";
import { BridgeFlow } from "../../bridge";
import { SendFlow, formatRawTokenAmount } from "../../send";
import { useTransactionExplorerUrl } from "../../send/queries/use-chain-info";
import { TokenDetailView } from "./TokenDetailView";
import { TrackingBoundary } from "../../../lib/sentry/index";

/**
 * `onBuy`가 reject됐는데 `onBuyDisabledMessage`가 따로 없을 때(=allow 통과 후
 * url/open 단계 실패) 띄우는 기본 안내. on-ramp가 차단된 지역 토스트와 동일 톤.
 */
const ONRAMP_BUY_FAILED_MESSAGE = "On-ramp is not available in your region.";

function getPinnedOrder(
  env: Environment,
  chainId: number,
  symbol: string,
): number | undefined {
  const chains = CHAIN_IDS[env];
  const s = symbol.toUpperCase();
  if (chainId === chains.cross && s.includes("ONEUSD")) return 0;
  if (chainId === chains.cross && s === "ONE") return 1;
  if (chainId === chains.bsc && s === "ONE") return 2;
  return undefined;
}

function parseChainIdNum(caip2: ChainId): number {
  return parseInt(caip2.split(":")[1], 10);
}

/**
 * 현재 페이지가 gametoken 사이트 안인지 판별.
 * apps.json(gametokenSites)의 URL prefix 하위에서만 브릿지 버튼이
 * 같은 창으로 이동하고, 그 외에는 모두 새창으로 연다.
 */
function isWithinGametokenSite(sites: string[] | undefined): boolean {
  if (typeof window === "undefined" || !sites?.length) return false;
  const { origin, pathname } = window.location;
  const current = origin + pathname;
  return sites.some(
    (site) => current === site || current.startsWith(`${site}/`),
  );
}

/** "127,475.34" → { main: "127,475", decimal: ".34" } */
function splitAmount(value: string): { main: string; decimal: string } {
  const dotIdx = value.lastIndexOf(".");
  if (dotIdx === -1) return { main: value, decimal: "" };
  return { main: value.slice(0, dotIdx), decimal: value.slice(dotIdx) };
}

/** 주소 기반 간단한 해시로 그라디언트 아바타 색을 결정. */
function getAvatarGradient(address: string): string {
  const hex = address.toLowerCase().replace(/^0x/, "");
  const seed = hex.length >= 8 ? hex.slice(0, 8) : hex.padEnd(8, "0");
  const h1 = parseInt(seed.slice(0, 4), 16) % 360;
  const h2 = (h1 + 60 + (parseInt(seed.slice(4, 8), 16) % 120)) % 360;
  return `linear-gradient(135deg, hsl(${h1} 85% 65%), hsl(${h2} 80% 55%))`;
}

/**
 * 여러 폰트 크기(통화기호/정수부/소수부)를 가진 값이 컨테이너 폭을 넘으면
 * 줄바꿈 대신 전체를 같은 비율로 축소하는 배율(0~1)을 계산한다. 배율은
 * `--wi-total-scale` CSS 변수로 내려 각 span 의 font-size(`calc(px * var)`)에
 * 곱해진다. 자연 폭은 clone 을 절대배치로 붙여(레이아웃 미영향, 폰트 상속 유지)
 * scale=1 기준으로 측정하므로 clamp/진동이 없다.
 */
function useFitScale(dep: string, minScale = 0.5) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const valueRef = React.useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = React.useState(1);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const el = valueRef.current;
    if (!container || !el) return;

    const measure = () => {
      const available = container.clientWidth;
      if (!available) return;

      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.setProperty("--wi-total-scale", "1");
      clone.style.position = "absolute";
      clone.style.left = "-9999px";
      clone.style.top = "-9999px";
      clone.style.visibility = "hidden";
      clone.style.maxWidth = "none";
      clone.style.width = "max-content";
      container.appendChild(clone);
      const natural = clone.offsetWidth;
      container.removeChild(clone);

      const next =
        natural > available ? Math.max(minScale, available / natural) : 1;
      setScale(next);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [dep, minScale]);

  return { containerRef, valueRef, scale };
}

export interface WalletInfoContentProps {
  align?: "start" | "center" | "end";
  sideOffset?: number;
  className?: string;
  children?: React.ReactNode;
}

export function WalletInfoContent({
  align = "end",
  sideOffset = 12,
  className,
  children,
}: WalletInfoContentProps) {
  const {
    env,
    theme,
    isMobile,
    open,
    drawerDirection,
    modal,
    showBalance,
    showForgeToken,
    showGameToken,
    showQR,
    showBridge,
    qrLogoSrc,
    walletAddress,
    accountName,
    sendAccounts,
    profileImageUrl,
    preferredTokens,
    close,
    onSelectWallet,
    onCopyAddress,
    onBuy,
    onDisconnect,
    disconnectLabel,
    termsUrl,
    termsLabel,
    privacyUrl,
    privacyLabel,
    portalStyle,
    navContent,
    navPosition,
    footerContent,
    view,
    setView: setViewFromCtx,
    selectedSendToken,
    setSelectedSendToken,
    selectedDetailToken,
    setSelectedDetailToken,
    showPortfolio,
    portfolioTitle,
    portfolioSections,
    showTotalAssets,
    totalAssetsLabel,
    sendTransaction,
    getTransactionReceipt,
    estimateGas,
    onOutlink,
    lpBalanceReader,
    stakingRewardsReader,
    bridgeTokens,
    bridgeHistory,
    getBridgeQuote,
    getBridgeToTokens,
    getBridgeApproval,
    approveBridge,
    submitBridge,
    onReceive,
    onSend,
    onBuyDisabledMessage,
    onBridgeDisabledMessage,
    onSendDisabledMessage,
  } = useWalletInfoContext();

  // Buy 카드용 ad-hoc 토스트. on-ramp가 차단된 국가 등 시각적으로는 비활성이지만
  // 클릭 시 사용자에게 안내 메시지를 보여줘야 할 때 사용. 4초 후 자동 사라짐.
  const [actionToast, setActionToast] = React.useState<string | null>(null);
  const actionToastTimerRef =
    React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const showActionToast = React.useCallback((message: string) => {
    setActionToast(message);
    clearTimeout(actionToastTimerRef.current);
    actionToastTimerRef.current = setTimeout(() => setActionToast(null), 4000);
  }, []);
  React.useEffect(
    () => () => clearTimeout(actionToastTimerRef.current),
    [],
  );

  const buyDisabledByMessage = !!onBuyDisabledMessage;
  const handleBuyClick = React.useCallback(() => {
    if (buyDisabledByMessage) {
      showActionToast(onBuyDisabledMessage!);
      return;
    }
    // onBuy가 Promise를 반환하면(connect-kit의 on-ramp 자동 wiring 등) reject 시
    // allow/url API 실패를 사용자에게 안내한다. `onBuyDisabledMessage`가 없을 때만
    // 도달하므로(allow 통과 케이스), 동일 톤의 기본 안내 메시지를 토스트로 재사용한다.
    const result = onBuy?.();
    if (result && typeof (result as Promise<void>).then === "function") {
      void (result as Promise<void>).catch(() => {
        showActionToast(onBuyDisabledMessage ?? ONRAMP_BUY_FAILED_MESSAGE);
      });
    }
  }, [buyDisabledByMessage, onBuyDisabledMessage, onBuy, showActionToast]);

  // Bridge / Send 게이트. 연결된 지갑이 CROSSx 계열이 아닐 때 react 레이어가
  // 안내 메시지를 주입한다(Buy 비활성 패턴과 동일). 메시지가 있으면 해당 액션은
  // 시각적으로 비활성 처리되고, 클릭 시 토스트만 띄운 뒤 실제 동작은 막힌다.
  const bridgeDisabledByMessage = !!onBridgeDisabledMessage;
  const sendDisabledByMessage = !!onSendDisabledMessage;

  const { tokens: rawTokens, isLoading } = useTokenBalance(
    env,
    walletAddress,
    open && showBalance,
  );
  const { statsMap } = useTokenStats(env, showBalance);
  const { aprPercent: crossdAprPercent } = useCrossdApr();
  const { categoryMap, infoMap } = useTokenInfo(env, showBalance);
  const showTotal = showBalance && showTotalAssets;
  const {
    displayValue: totalDisplay,
    isLowerBound: totalIsLowerBound,
    isLoading: totalLoading,
  } = useTotalValue(env, walletAddress, open && showTotal);
  const [totalHidden, setTotalHidden] = React.useState(false);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = React.useState(0);
  const { holdings: forgeHoldings, isLoading: isForgeLoading } =
    useForgeHoldings(env, walletAddress, open && showBalance && showForgeToken);

  const forgeTokenAddresses = React.useMemo(
    () => forgeHoldings.map((h) => h.token),
    [forgeHoldings],
  );
  const { imageMap: forgeImageMap } = useForgeTokenImages(
    env,
    forgeTokenAddresses,
    showForgeToken && forgeHoldings.length > 0,
  );

  const tokens = React.useMemo(() => {
    const pinned: TokenBalance[] = [];
    const preferred: TokenBalance[] = [];
    const rest: TokenBalance[] = [];

    const prefKeyMap = new Map(
      (preferredTokens ?? []).map((t, i) => [
        `${parseChainIdNum(t.chainId)}:${t.address.toLowerCase()}`,
        i,
      ]),
    );

    for (const token of rawTokens) {
      const isPinned =
        getPinnedOrder(env, token.chainId, token.symbol) !== undefined;
      const tokenKey = `${token.chainId}:${token.address.toLowerCase()}`;
      const prefIdx = prefKeyMap.get(tokenKey);
      const balance = new BigNumber(token.quantity.numeric).shiftedBy(
        -token.quantity.decimals,
      );

      if (isPinned) {
        pinned.push(token);
      } else if (prefIdx !== undefined) {
        preferred.push(token);
      } else if (balance.isGreaterThan(0)) {
        if (!showGameToken) {
          const cat = categoryMap.get(
            buildCategoryKey(token.chainId, token.address),
          );
          if (cat === "game") continue;
        }
        rest.push(token);
      }
    }

    pinned.sort((a, b) => {
      const aIdx = getPinnedOrder(env, a.chainId, a.symbol) ?? 99;
      const bIdx = getPinnedOrder(env, b.chainId, b.symbol) ?? 99;
      return aIdx - bIdx;
    });

    preferred.sort((a, b) => {
      const aIdx =
        prefKeyMap.get(`${a.chainId}:${a.address.toLowerCase()}`) ?? 99;
      const bIdx =
        prefKeyMap.get(`${b.chainId}:${b.address.toLowerCase()}`) ?? 99;
      return aIdx - bIdx;
    });

    rest.sort((a, b) => {
      const statA = statsMap.get(buildStatsKey(a.chainId, a.address));
      const statB = statsMap.get(buildStatsKey(b.chainId, b.address));
      const valueA = new BigNumber(a.quantity.numeric)
        .shiftedBy(-a.quantity.decimals)
        .multipliedBy(statA?.price ?? 0);
      const valueB = new BigNumber(b.quantity.numeric)
        .shiftedBy(-b.quantity.decimals)
        .multipliedBy(statB?.price ?? 0);
      return valueB.minus(valueA).toNumber();
    });

    return [...pinned, ...preferred, ...rest];
  }, [rawTokens, env, statsMap, showGameToken, categoryMap, preferredTokens]);

  const sortedForgeHoldings = React.useMemo(() => {
    if (!showForgeToken) return [];
    const balanceAddressSet = new Set(
      rawTokens.map((t) => t.address.toLowerCase()),
    );
    return [...forgeHoldings]
      .filter((h) => !balanceAddressSet.has(h.token.toLowerCase()))
      .sort((a, b) => {
        const valueA = new BigNumber(a.value);
        const valueB = new BigNumber(b.value);
        return valueB.minus(valueA).toNumber();
      });
  }, [forgeHoldings, showForgeToken, rawTokens]);

  const hasTransitioned = React.useRef(false);
  const [sendToast, setSendToast] = React.useState<{
    status: "pending" | "success" | "error";
    txHash: `0x${string}`;
    chainId: number;
    description: string;
  } | null>(null);
  const sendToastTimerRef =
    React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const toastChainId =
    sendToast?.chainId ?? selectedSendToken?.chainId ?? tokens[0]?.chainId ?? 0;
  const { transactionUrl: sendToastUrl } = useTransactionExplorerUrl(
    env,
    toastChainId,
    sendToast?.txHash,
    Boolean(sendToast?.txHash && toastChainId),
  );

  const setView = React.useCallback(
    (v: WalletInfoView) => {
      hasTransitioned.current = true;
      setViewFromCtx(v);
    },
    [setViewFromCtx],
  );
  const [sendInitialStep, setSendInitialStep] =
    React.useState<"token" | "recipient">("recipient");

  const handleBackToWallet = React.useCallback(() => {
    setSelectedSendToken(null);
    setSendInitialStep("recipient");
    setView("wallet");
  }, [setSelectedSendToken, setView]);

  const handleSelectSendToken = React.useCallback(
    (token: TokenBalance, initialStep: "token" | "recipient" = "recipient") => {
      // 토큰 행 탭도 전송 진입점이므로 동일 게이트를 적용한다.
      if (sendDisabledByMessage) {
        showActionToast(onSendDisabledMessage!);
        return;
      }
      setSendInitialStep(initialStep);
      setSelectedSendToken(token);
      setView("send");
    },
    [
      sendDisabledByMessage,
      onSendDisabledMessage,
      showActionToast,
      setSelectedSendToken,
      setView,
    ],
  );

  // 토큰 행 탭 → 해당 토큰의 상세/이력 화면. (기존엔 바로 전송으로 진입했다.)
  const handleSelectTokenDetail = React.useCallback(
    (token: TokenBalance) => {
      setSelectedDetailToken(token);
      setView("token-detail");
    },
    [setSelectedDetailToken, setView],
  );

  // 상세화면 → 지갑으로 복귀. 선택된 상세 토큰을 정리한다.
  const handleBackFromDetail = React.useCallback(() => {
    setSelectedDetailToken(null);
    setView("wallet");
  }, [setSelectedDetailToken, setView]);

  // 상세화면 안의 Send 버튼: 기존 전송 플로우를 recipient 스텝으로 진입한다.
  const handleSendFromDetail = React.useCallback(
    (token: TokenBalance) => {
      handleSelectSendToken(token, "recipient");
    },
    [handleSelectSendToken],
  );

  // 브릿지 버튼 이동 대상 — apps.json(gametokenBridge)이 단일 소스.
  // 로드 전/실패 시 undefined이며 그동안 버튼 클릭은 no-op.
  const remoteBridgeWebUrl = useRemoteConfigValue<AppsConfig, string>(
    "apps.json",
    env,
    (config) => resolveEnvValue(config?.gametokenBridge, env),
  );

  // 브릿지 버튼을 같은 창으로 여는 gametoken 사이트 URL prefix 목록.
  const remoteGametokenSites = useRemoteConfigValue<AppsConfig, string[]>(
    "apps.json",
    env,
    (config) => config?.gametokenSites,
  );

  // onBridge 콜백/임베디드 BridgeFlow(setView("bridge"))를 무시하고 항상
  // gametoken 브릿지 웹으로 이동한다 — apps.json(gametokenBridge)이 단일 소스.
  // 단, gametoken 사이트(apps.json gametokenSites) 안이면 새창 대신 같은 창 이동.
  const handleBridgeClick = React.useCallback(() => {
    if (bridgeDisabledByMessage) {
      showActionToast(onBridgeDisabledMessage!);
      return;
    }
    if (!remoteBridgeWebUrl) return;
    if (isWithinGametokenSite(remoteGametokenSites)) {
      window.location.assign(remoteBridgeWebUrl);
      return;
    }
    window.open(remoteBridgeWebUrl, "_blank", "noopener,noreferrer");
  }, [
    bridgeDisabledByMessage,
    onBridgeDisabledMessage,
    remoteBridgeWebUrl,
    remoteGametokenSites,
    showActionToast,
  ]);

  const handleSendSubmitted = React.useCallback((info: SendSubmittedInfo) => {
    clearTimeout(sendToastTimerRef.current);
    setSelectedSendToken(null);
    setView("wallet");
    setSendToast({
      status: "pending",
      txHash: info.txHash,
      chainId: info.chainId,
      description: `${info.amount} ${info.tokenSymbol} to ${shortenAddress(info.recipient)}`,
    });
  }, [setSelectedSendToken, setView]);

  const handleConfirmSendSuccess = React.useCallback((txHash?: `0x${string}`) => {
    const chainId = selectedSendToken?.chainId;
    setSelectedSendToken(null);
    setView("wallet");
    if (!txHash || !chainId) return;
    setSendToast((current) => ({
      status: "success",
      txHash,
      chainId,
      description: current?.txHash === txHash
        ? current.description
        : "Tap to see your transaction",
    }));
    clearTimeout(sendToastTimerRef.current);
    sendToastTimerRef.current = setTimeout(() => setSendToast(null), 5000);
  }, [selectedSendToken?.chainId, setSelectedSendToken, setView]);

  const handleSendFailed = React.useCallback((info: SendFailedInfo) => {
    const txHash = info.txHash;
    if (!txHash) return;
    setSendToast({
      status: "error",
      txHash,
      chainId: info.chainId,
      description: info.message,
    });
    clearTimeout(sendToastTimerRef.current);
    sendToastTimerRef.current = setTimeout(() => setSendToast(null), 5000);
  }, []);

  // QR 버튼 동작: 외부 onReceive prop 우선, 미주입 + showQR=true면
  // 내장 QR view로 전환.
  const effectiveReceive = React.useMemo<(() => void) | undefined>(() => {
    if (onReceive) return onReceive;
    if (showQR) return () => setView("qr");
    return undefined;
  }, [onReceive, showQR, setView]);

  // Send 버튼 동작: 외부 onSend prop 우선, 미주입이면 토큰 선택 스텝으로
  // 진입 (토큰을 먼저 고른 뒤 수신자 → 금액 순으로 진행).
  const effectiveSend = React.useMemo<(() => void) | undefined>(() => {
    if (onSend) return onSend;
    if (tokens.length === 0) return undefined; // 로딩 중/빈 리스트면 비활성
    const first = tokens[0];
    return () => handleSelectSendToken(first, "token");
  }, [onSend, tokens, handleSelectSendToken]);

  // Send 액션 카드 클릭. 게이트가 걸려 있으면 토스트만 띄우고 실제 전송 진입은
  // 막는다(`effectiveSend`가 외부 onSend를 그대로 반환하는 경우도 포함).
  const handleSendClick = React.useCallback(() => {
    if (sendDisabledByMessage) {
      showActionToast(onSendDisabledMessage!);
      return;
    }
    effectiveSend?.();
  }, [sendDisabledByMessage, onSendDisabledMessage, effectiveSend, showActionToast]);

  React.useEffect(() => {
    if (!open) {
      hasTransitioned.current = false;
      setSendInitialStep("recipient");
    }
  }, [open]);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isMobile) return;
    const stop = (e: PointerEvent) => {
      if (e.pointerType === "touch") e.stopPropagation();
    };
    el.addEventListener("pointerdown", stop);
    return () => el.removeEventListener("pointerdown", stop);
  }, [isMobile]);

  const [copied, setCopied] = React.useState(false);
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const [accountModalOpen, setAccountModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (!accountModalOpen) return;
    const handleClose = () => setAccountModalOpen(false);
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAccountModalOpen(false); };
    const timer = setTimeout(() => {
      // capture:true → stopPropagation 으로 막힌 이벤트도 잡음
      document.addEventListener('mousedown', handleClose, true);
      document.addEventListener('keydown', handleKey, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClose, true);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [accountModalOpen]);

  // Bridge 모드에서는 네이티브 지갑 선택기가 웹 컨텍스트 밖에 떠서
  // 위의 mousedown/ESC 리스너가 트리거되지 않는다. 지갑이 실제로 바뀌면
  // (walletAddress 변경) chevron(accountModalOpen)을 닫아 ^ 가 정상 복귀하도록 한다.
  React.useEffect(() => {
    setAccountModalOpen(false);
  }, [walletAddress]);

  React.useEffect(() => {
    return () => clearTimeout(copyTimerRef.current);
  }, []);

  React.useEffect(() => {
    return () => clearTimeout(sendToastTimerRef.current);
  }, []);

  React.useEffect(() => {
    const footerEl = footerRef.current;
    if (!footerEl || footerContent === null) {
      setFooterHeight(0);
      return;
    }

    const updateFooterHeight = () => {
      setFooterHeight(footerEl.offsetHeight);
    };

    updateFooterHeight();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateFooterHeight);
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [footerContent]);

  const handleCopyAddress = React.useCallback(async () => {
    if (copied) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
      onCopyAddress?.(walletAddress, true);
    } catch {
      onCopyAddress?.(walletAddress, false);
    }
  }, [walletAddress, onCopyAddress, copied]);

  const handleImageLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.setAttribute("data-loaded", "true");
    },
    [],
  );

  const avatarStyle = React.useMemo<React.CSSProperties>(
    () => ({ background: getAvatarGradient(walletAddress) }),
    [walletAddress],
  );

  const resolvedProfileUrl = React.useMemo(() => {
    if (profileImageUrl) return profileImageUrl;
    const bg = theme === "dark" ? "1A1A2E" : "F3F6F8";
    return getPortraitUrl(env, walletAddress, { size: 120, bg });
  }, [profileImageUrl, env, walletAddress, theme]);

  const totalSplit = React.useMemo(
    () => splitAmount(totalDisplay),
    [totalDisplay],
  );

  // 총자산 금액이 길어 폭을 넘으면 줄바꿈 대신 폰트를 비율로 축소한다.
  const {
    containerRef: totalBodyRef,
    valueRef: totalValueRef,
    scale: totalScale,
  } = useFitScale(totalHidden ? "••••••" : totalDisplay);

  const legalLinks =
    termsUrl || privacyUrl ? (
      <div className="wi-legal-links">
        {termsUrl && (
          <a
            className="wi-legal-link"
            href={termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-track="terms_link"
            onClick={(e) =>
              handleOutlinkClick(
                e,
                termsUrl,
                { category: "terms", origin: "terms" },
                onOutlink,
              )
            }
          >
            {termsLabel}
          </a>
        )}
        {termsUrl && privacyUrl && (
          <span className="wi-legal-sep" aria-hidden>
            ·
          </span>
        )}
        {privacyUrl && (
          <a
            className="wi-legal-link"
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-track="privacy_link"
            onClick={(e) =>
              handleOutlinkClick(
                e,
                privacyUrl,
                { category: "privacy", origin: "privacy" },
                onOutlink,
              )
            }
          >
            {privacyLabel}
          </a>
        )}
      </div>
    ) : null;

  const defaultDisconnectButton = onDisconnect ? (
    <button
      type="button"
      className="wi-disconnect-btn"
      data-track="disconnect"
      onClick={() => {
        onDisconnect();
        close();
      }}
    >
      <DisconnectIcon size={16} />
      <span className="wi-disconnect-label">{disconnectLabel}</span>
    </button>
  ) : null;
  const showDefaultQuickActions = showBalance && !navContent;

  const defaultQuickActions = showDefaultQuickActions ? (
    <div className="wi-action-row" aria-label="Wallet actions">
      <button
        type="button"
        className={cn(
          "wi-action-card",
          buyDisabledByMessage && "wi-action-card--dimmed",
        )}
        onClick={handleBuyClick}
        disabled={!onBuy && !buyDisabledByMessage}
        aria-disabled={buyDisabledByMessage || undefined}
        aria-label="Buy"
        data-track="buy"
      >
        <BuyActionIcon size={20} />
        <span>Buy</span>
      </button>
      {showBridge ? (
        <button
          type="button"
          className={cn(
            "wi-action-card",
            bridgeDisabledByMessage && "wi-action-card--dimmed",
          )}
          onClick={handleBridgeClick}
          aria-disabled={bridgeDisabledByMessage || undefined}
          aria-label="Bridge"
          data-track="bridge_open"
        >
          <BridgeActionIcon size={20} />
          <span>Bridge</span>
        </button>
      ) : (
        <button
          type="button"
          className="wi-action-card"
          onClick={effectiveReceive}
          disabled={!effectiveReceive}
          aria-label="Receive"
          data-track="receive"
        >
          <QRIcon size={20} />
          <span>Receive</span>
        </button>
      )}
      <button
        type="button"
        className={cn(
          "wi-action-card",
          sendDisabledByMessage && "wi-action-card--dimmed",
        )}
        onClick={handleSendClick}
        disabled={!sendDisabledByMessage && !effectiveSend}
        aria-disabled={sendDisabledByMessage || undefined}
        aria-label="Send"
        data-track="send_open"
      >
        <SendActionIcon size={20} />
        <span>Send</span>
      </button>
      {showPortfolio ? (
        <button
          type="button"
          className="wi-action-card"
          onClick={() => setView("portfolio")}
          aria-label="View portfolio"
          data-track="portfolio_open"
        >
          <PortfolioActionIcon size={20} />
          <span>Portfolio</span>
        </button>
      ) : showBridge ? (
        // Portfolio가 꺼져 있으면 이 슬롯을 Receive(QR)로 대체한다. Bridge가
        // slot 2를 차지할 때만 노출해 Receive 카드가 중복되지 않도록 한다.
        <button
          type="button"
          className="wi-action-card"
          onClick={effectiveReceive}
          disabled={!effectiveReceive}
          aria-label="Receive"
          data-track="receive"
        >
          <QRIcon size={20} />
          <span>Receive</span>
        </button>
      ) : null}
    </div>
  ) : null;

  const walletView = (
    <div
      key="wallet"
      className={cn(
        "wi-view-inner",
        hasTransitioned.current && "wi-view-wallet",
      )}
    >
      {showTotal && (
        <div className="wi-total-section">
          <div className="wi-total-head">
            <span className="wi-total-label">{totalAssetsLabel}</span>
            <span
              role="button"
              tabIndex={0}
              className="wi-total-eye"
              onClick={(e) => {
                e.stopPropagation();
                setTotalHidden((prev) => !prev);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  setTotalHidden((prev) => !prev);
                }
              }}
              aria-label={totalHidden ? "Show balance" : "Hide balance"}
              data-track="balance_toggle"
            >
              {totalHidden ? <EyeClosedIcon /> : <EyeOpenIcon />}
            </span>
          </div>
          <div className="wi-total-body" ref={totalBodyRef}>
            {totalLoading ? (
              <div
                className="wi-skeleton-block wi-total-skeleton"
                style={{ width: 160, height: 42 }}
              />
            ) : (
              <div
                className="wi-total-value"
                data-hidden={totalHidden || undefined}
                ref={totalValueRef}
                style={
                  { "--wi-total-scale": totalScale } as React.CSSProperties
                }
              >
                {totalHidden ? (
                  <span className="wi-total-mask">••••••</span>
                ) : (
                  <>
                    {totalIsLowerBound && (
                      <span className="wi-total-lte">≤</span>
                    )}
                    <span className="wi-total-currency">$</span>
                    <span className="wi-total-amount">
                      {totalSplit.main}
                    </span>
                    {totalSplit.decimal && (
                      <span className="wi-total-decimal">
                        {totalSplit.decimal}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {navPosition === "top" && (
        navContent ? (
          <div className="wi-nav-fixed">{navContent}</div>
        ) : (
          defaultQuickActions
        )
      )}

      {showBalance && (
        <div ref={scrollRef} className="wi-scrollable-body" data-vaul-no-drag>
          <div className="wi-token-list">
            {isLoading &&
              Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="wi-skeleton-item">
                  <div className="wi-skeleton-left">
                    <div className="wi-skeleton-block wi-skeleton-icon" />
                    <div className="wi-skeleton-lines">
                      <div
                        className="wi-skeleton-block"
                        style={{ width: 64, height: 14 }}
                      />
                      <div
                        className="wi-skeleton-block"
                        style={{ width: 80, height: 12 }}
                      />
                    </div>
                  </div>
                  <div className="wi-skeleton-right">
                    <div
                      className="wi-skeleton-block"
                      style={{ width: 72, height: 14 }}
                    />
                    <div
                      className="wi-skeleton-block"
                      style={{ width: 48, height: 12 }}
                    />
                  </div>
                </div>
              ))}

            {!isLoading && tokens.length === 0 && !showForgeToken && (
              <div className="wi-empty">No Tokens Found</div>
            )}

            {tokens.map((token) => {
              const stat = statsMap.get(
                buildStatsKey(token.chainId, token.address),
              );
              const change = stat
                ? parseFloat(stat.percent_change_24h)
                : null;
              const isPositive = change !== null && change >= 0;
              const chainBadgeUrl = getChainBadgeUrl(env, token.chainId);
              const shouldShowApr = isCrossdTokenAddress(token.address);

              return (
                <button
                  type="button"
                  key={`${token.address}-${token.chainId}`}
                  className="wi-token-item wi-token-item--button"
                  onClick={() => handleSelectTokenDetail(token)}
                  aria-label={`View ${token.symbol} detail`}
                  data-track="token_detail"
                  data-track-id={token.symbol}
                >
                  <div className="wi-token-left">
                    <div className="wi-token-icon-wrap">
                      <img
                        src={token.icon_url}
                        alt={token.symbol}
                        className="wi-token-icon"
                        width={32}
                        height={32}
                        loading="lazy"
                        draggable={false}
                        onLoad={handleImageLoad}
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://contents.crosstoken.io/wallet/token/images/FT.png";
                        }}
                      />
                      {chainBadgeUrl && (
                        <img
                          src={chainBadgeUrl}
                          alt=""
                          aria-hidden
                          className="wi-token-chain-badge"
                          width={14}
                          height={14}
                          loading="lazy"
                          draggable={false}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      )}
                    </div>
                    <div className="wi-token-info">
                      <span className="wi-token-name-row">
                        <span className="wi-token-name">{token.symbol}</span>
                        {shouldShowApr && crossdAprPercent !== null && (
                          <span className="wi-token-apr">
                            {formatCrossdAprPercent(crossdAprPercent)}
                          </span>
                        )}
                      </span>
                      {stat && (
                        <div className="wi-token-price-row">
                          <span className="wi-token-price">
                            {formatPrice(stat.price)}
                          </span>
                          <span
                            className="wi-token-change"
                            data-positive={isPositive}
                            data-zero={change === 0 || undefined}
                          >
                            {change !== null &&
                              (change === 0
                                ? "0%"
                                : `${isPositive ? "+" : "-"}${(isPositive ? Math.floor : Math.ceil)(Math.abs(change) * 100) / 100}%`)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="wi-token-right">
                    <span className="wi-token-balance">
                      {formatWei(
                        token.quantity.numeric,
                        token.quantity.decimals,
                      )}
                    </span>
                    {stat && (
                      <span className="wi-token-value">
                        {formatTokenValue(
                          token.quantity.numeric,
                          token.quantity.decimals,
                          stat.price,
                        )}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {showForgeToken &&
              isForgeLoading &&
              Array.from({ length: 3 }, (_, i) => (
                <div key={`forge-sk-${i}`} className="wi-skeleton-item">
                  <div className="wi-skeleton-left">
                    <div className="wi-skeleton-block wi-skeleton-icon" />
                    <div className="wi-skeleton-lines">
                      <div
                        className="wi-skeleton-block"
                        style={{ width: 64, height: 14 }}
                      />
                      <div
                        className="wi-skeleton-block"
                        style={{ width: 80, height: 12 }}
                      />
                    </div>
                  </div>
                  <div className="wi-skeleton-right">
                    <div
                      className="wi-skeleton-block"
                      style={{ width: 72, height: 14 }}
                    />
                    <div
                      className="wi-skeleton-block"
                      style={{ width: 48, height: 12 }}
                    />
                  </div>
                </div>
              ))}

            {showForgeToken &&
              !isForgeLoading &&
              !isLoading &&
              tokens.length === 0 &&
              sortedForgeHoldings.length === 0 && (
                <div className="wi-empty">No Tokens Found</div>
              )}

            {showForgeToken &&
              sortedForgeHoldings.map((holding) => {
                const imgUrl = forgeImageMap.get(holding.token) || "";
                const chainBadgeUrl = getChainBadgeUrl(
                  env,
                  CHAIN_IDS[env].cross,
                );
                return (
                  <div key={`forge-${holding.token}`} className="wi-token-item">
                    <div className="wi-token-left">
                      <div className="wi-token-icon-wrap">
                        <img
                          src={
                            imgUrl ||
                            "https://contents.crosstoken.io/wallet/token/images/FT.png"
                          }
                          alt={holding.token_name}
                          className="wi-token-icon"
                          width={32}
                          height={32}
                          loading="lazy"
                          draggable={false}
                          onLoad={handleImageLoad}
                          onError={(e) => {
                            e.currentTarget.src =
                              "https://contents.crosstoken.io/wallet/token/images/FT.png";
                          }}
                        />
                        {chainBadgeUrl && (
                          <img
                            src={chainBadgeUrl}
                            alt=""
                            aria-hidden
                            className="wi-token-chain-badge"
                            width={14}
                            height={14}
                            loading="lazy"
                            draggable={false}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                      </div>
                      <div className="wi-token-info">
                        <span className="wi-token-name">
                          {holding.token_name}
                        </span>
                        <div className="wi-token-price-row">
                          <span className="wi-token-price">
                            {formatWei(holding.current_price, 18)} CROSS
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="wi-token-right">
                      <span className="wi-token-balance">
                        {formatWei(holding.balance, 18)}
                      </span>
                      <span className="wi-token-value">
                        {formatPrice(holding.current_value_usd)}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {navPosition === "bottom" && navContent && (
        <div className="wi-nav-fixed">{navContent}</div>
      )}

      {defaultDisconnectButton && (
        <div className="wi-wallet-disconnect-row">{defaultDisconnectButton}</div>
      )}
    </div>
  );

  const portfolioView = showPortfolio ? (
    <div
      key="portfolio"
      className={cn(
        "wi-view-inner wi-portfolio-body",
        hasTransitioned.current && "wi-view-portfolio",
      )}
    >
      <WalletPortfolioBody
        env={env}
        theme={theme}
        walletAddress={walletAddress}
        showHeader={false}
        onOutlink={onOutlink}
        lpBalanceReader={lpBalanceReader}
        stakingRewardsReader={stakingRewardsReader}
        sendTransaction={sendTransaction}
        estimateGas={estimateGas}
        sections={portfolioSections}
      />
    </div>
  ) : null;

  const sendView = selectedSendToken ? (
    <div
      key="send"
      className={cn(
        "wi-view-inner wi-send-body",
        hasTransitioned.current && "wi-view-send",
      )}
    >
      <SendFlow
        env={env}
        theme={theme}
        walletAddress={walletAddress}
        accountName={accountName}
        accounts={sendAccounts}
        initialStep={sendInitialStep}
        token={selectedSendToken}
        tokens={tokens}
        onTokenChange={setSelectedSendToken}
        sendTransaction={sendTransaction}
        getTransactionReceipt={getTransactionReceipt}
        estimateGas={estimateGas}
        getTokenPriceUsd={(t) => {
          const s = statsMap.get(buildStatsKey(t.chainId, t.address));
          if (!s?.price) return undefined;
          const n = parseFloat(s.price);
          return Number.isFinite(n) ? n : undefined;
        }}
        onClose={close}
        onBackToWallet={handleBackToWallet}
        onSubmitted={handleSendSubmitted}
        onSuccess={handleConfirmSendSuccess}
        onFailed={handleSendFailed}
        onOutlink={onOutlink}
      />
    </div>
  ) : null;

  const resolvedBridgeTokens = React.useMemo<BridgeToken[]>(() => {
    if (bridgeTokens) return bridgeTokens;

    return tokens.map((token) => {
      const stat = statsMap.get(buildStatsKey(token.chainId, token.address));
      const price = stat?.price ? parseFloat(stat.price) : undefined;

      return {
        name: token.name,
        symbol: token.symbol,
        chainId: token.chainId,
        address: token.address,
        decimals: token.quantity.decimals,
        balance: formatRawTokenAmount(
          token.quantity.numeric,
          token.quantity.decimals,
        ),
        iconUrl: token.icon_url,
        priceUsd: Number.isFinite(price) ? price : undefined,
      };
    });
  }, [bridgeTokens, statsMap, tokens]);

  const bridgeView = (
    <div
      key="bridge"
      className={cn(
        "wi-view-inner wi-bridge-body",
        hasTransitioned.current && "wi-view-send",
      )}
    >
      <BridgeFlow
        env={env}
        walletAddress={walletAddress}
        tokens={resolvedBridgeTokens}
        history={bridgeHistory}
        termsUrl={termsUrl}
        getQuote={getBridgeQuote}
        getToTokens={getBridgeToTokens}
        getApproval={getBridgeApproval}
        approveBridge={approveBridge}
        submitBridge={submitBridge}
        onClose={close}
        onBackToWallet={handleBackToWallet}
      />
    </div>
  );

  const tokenDetailView = selectedDetailToken ? (
    <div
      key="token-detail"
      className={cn(
        "wi-view-inner wi-token-detail-body",
        hasTransitioned.current && "wi-view-send",
      )}
    >
      <TokenDetailView
        env={env}
        isMobile={isMobile}
        token={selectedDetailToken}
        walletAddress={walletAddress}
        stat={statsMap.get(
          buildStatsKey(
            selectedDetailToken.chainId,
            selectedDetailToken.address,
          ),
        )}
        tokenInfo={infoMap.get(
          buildCategoryKey(
            selectedDetailToken.chainId,
            selectedDetailToken.address,
          ),
        )}
        showBalance={showBalance}
        onBack={handleBackFromDetail}
        onClose={close}
        onSend={handleSendFromDetail}
        onOutlink={onOutlink}
      />
    </div>
  ) : null;

  // "My Wallet" 라벨은 멀티 계정 개념이 없는 CROSS 1.0 SDK 세션에서 쓰는
  // 표식이라 chevron 을 눌러도 selectWallet 이 의미 있는 동작을 하지 않는다.
  // 호출측이 onSelectWallet 을 그래도 넘기는 경우를 대비해 라벨 기준으로
  // 클릭/꺾쇠를 차단한다.
  const isSwitchableAccount =
    !!onSelectWallet && accountName?.trim().toLowerCase() !== "my wallet";

  const receiveAccountContent = (
    <>
      <span className="wi-receive-avatar">
        <span className="wi-avatar-fallback" style={avatarStyle} />
        {resolvedProfileUrl && (
          <img
            src={resolvedProfileUrl}
            alt=""
            className="wi-avatar-img"
            width={24}
            height={24}
            draggable={false}
            onLoad={handleImageLoad}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
      </span>
      <span className="wi-receive-account-name">
        {accountName ?? "Account 1"}
      </span>
      {isSwitchableAccount && (
        <span className="wi-receive-chevron" data-open={accountModalOpen} aria-hidden="true">
          <ChevronDownIcon size={16} />
        </span>
      )}
    </>
  );

  const qrView = (
    <div
      key="qr"
      className={cn(
        "wi-view-inner wi-qr-body",
        hasTransitioned.current && "wi-view-qr",
      )}
    >
      <div className="wi-receive-section">
        {isSwitchableAccount ? (
          <button
            type="button"
            className="wi-receive-account wi-receive-account--btn"
            onClick={() => { setAccountModalOpen(true); onSelectWallet?.(); }}
            aria-label="Switch account"
          >
            {receiveAccountContent}
          </button>
        ) : (
          <div className="wi-receive-account">{receiveAccountContent}</div>
        )}

        <div className="wi-qr-block">
          <div className="wi-qr-box">
            <QRCodeSVG
              value={walletAddress}
              size={180}
              level="H"
              marginSize={2}
              {...(qrLogoSrc && {
                imageSettings: {
                  src: qrLogoSrc,
                  height: 33,
                  width: 33,
                  excavate: true,
                },
              })}
            />
            {!qrLogoSrc && (
              <div className="wi-qr-logo">
                <QRLogoIcon size={33} />
              </div>
            )}
          </div>
          <p className="wi-qr-address">{walletAddress}</p>
        </div>

        <button
          type="button"
          className="wi-qr-copy-btn"
          onClick={handleCopyAddress}
          data-track="address_copy"
        >
          <span className="wi-qr-copy-icon">
            {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
          </span>
          <span className="wi-qr-copy-text">
            {copied ? "Copied!" : "Copy address"}
          </span>
        </button>
      </div>

      {legalLinks && <div className="wi-receive-footer">{legalLinks}</div>}
    </div>
  );

  const isSubView =
    view === "qr" || view === "portfolio" || view === "send" || view === "bridge";

  // 사용자 커스텀 Footer 슬롯은 기존 위치를 유지하고,
  // 기본 Disconnect/Legal 링크는 Figma 기준 위치에 직접 배치한다.
  const effectiveFooter =
    footerContent !== null ? (
      <div className="wi-builtin-footer">
        {footerContent}
      </div>
    ) : null;

  const accountTextBlock = (
    <div className="wi-account-text">
      {isSwitchableAccount ? (
        <button
          type="button"
          className="wi-account-row wi-account-row--btn"
          onClick={() => { setAccountModalOpen(true); onSelectWallet?.(); }}
          aria-label="Switch account"
          aria-expanded={accountModalOpen}
        >
          <span className="wi-account-name">{accountName ?? "Account 1"}</span>
          <span className="wi-account-switch" data-open={accountModalOpen} aria-hidden="true">
            <ChevronDownIcon size={20} />
          </span>
        </button>
      ) : (
        <div className="wi-account-row">
          <span className="wi-account-name">{accountName ?? "Account 1"}</span>
        </div>
      )}
      <button
        type="button"
        className="wi-address-row-btn"
        onClick={handleCopyAddress}
        aria-label="Copy address"
        data-track="address_copy"
      >
        <span className="wi-account-address">
          {shortenAddress(walletAddress)}
        </span>
        <span className="wi-account-copy" data-copied={copied}>
          {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
        </span>
      </button>
    </div>
  );

  const accountHeaderBlock = (
    <div className="wi-account-block">
      <div className="wi-avatar">
        <div className="wi-avatar-fallback" style={avatarStyle} />
        {resolvedProfileUrl && (
          <img
            src={resolvedProfileUrl}
            alt=""
            className="wi-avatar-img"
            width={28}
            height={28}
            draggable={false}
            onLoad={handleImageLoad}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
      </div>
      {accountTextBlock}
    </div>
  );

  const walletHeader = (
    <div className="wi-sticky-header">
      <div className="wi-header wi-header--wallet">
        {accountHeaderBlock}
        <div className="wi-header-actions">
          {showQR && showBridge && (
            <button
              type="button"
              className="wi-header-action wi-header-action--qr"
              onClick={effectiveReceive}
              aria-label="Show QR code"
              data-track="receive"
            >
              <QRIcon size={20} />
            </button>
          )}
          <button
            type="button"
            className="wi-header-action"
            onClick={close}
            aria-label="Close"
          >
            <CloseIcon size={24} />
          </button>
        </div>
      </div>
      <hr className="wi-divider" />
    </div>
  );

  const accountSubViewHeader = (
    <div className="wi-sticky-header">
      <div className="wi-header wi-header--wallet wi-header--sub-account">
        <button
          type="button"
          className="wi-header-back"
          onClick={handleBackToWallet}
          aria-label="Back"
        >
          <BackIcon size={24} />
        </button>
        <div className="wi-account-block wi-account-block--sub-account">
          {accountTextBlock}
        </div>
        <button
          type="button"
          className="wi-header-action"
          onClick={close}
          aria-label="Close"
        >
          <CloseIcon size={24} />
        </button>
      </div>
      <hr className="wi-divider" />
    </div>
  );

  const receiveHeader = (
    <div className="wi-sticky-header">
      <div className="wi-header wi-header--receive">
        <button
          type="button"
          className="wi-header-back"
          onClick={handleBackToWallet}
          aria-label="Back"
        >
          <BackIcon size={24} />
        </button>
        <span className="wi-receive-title">Receive</span>
        <button
          type="button"
          className="wi-header-action"
          onClick={close}
          aria-label="Close"
        >
          <CloseIcon size={24} />
        </button>
      </div>
    </div>
  );

  const contentBody = (
    <>
      {children}
      {view === "qr"
        ? receiveHeader
        : view === "send" || view === "bridge" || view === "token-detail"
          ? null
          : isSubView
            ? accountSubViewHeader
            : walletHeader}

      <div className="wi-view-container">
        {view === "wallet" && walletView}
        {view === "qr" && qrView}
        {view === "portfolio" && portfolioView}
        {view === "send" && sendView}
        {view === "bridge" && bridgeView}
        {view === "token-detail" && tokenDetailView}
      </div>

      {view === "wallet" && actionToast && (
        <div className="wi-action-toast" role="status" aria-live="polite">
          {actionToast}
        </div>
      )}

      {view === "wallet" && sendToast && (
        <a
          className="wi-send-toast"
          href={sendToastUrl ?? "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!sendToastUrl) {
              e.preventDefault();
              return;
            }
            handleOutlinkClick(
              e,
              sendToastUrl,
              {
                category: "send",
                origin: "send-transaction",
                payload: {
                  chainId: sendToast.chainId,
                  txHash: sendToast.txHash,
                },
              },
              onOutlink,
            );
          }}
        >
          <span
            className="wi-send-toast-icon"
            data-status={sendToast.status}
            aria-hidden
          >
            {sendToast.status === "pending" ? (
              <span className="wi-send-toast-spinner" />
            ) : sendToast.status === "error" ? (
              <span className="wi-send-toast-error-dot" />
            ) : (
              <ToastCheckIcon size={20} />
            )}
          </span>
          <span className="wi-send-toast-copy">
            <span className="wi-send-toast-title">
              {sendToast.status === "pending"
                ? "Sending..."
                : sendToast.status === "error"
                  ? "Send failed"
                  : "Send complete"}
            </span>
            <span className="wi-send-toast-desc">
              {sendToast.status === "pending"
                ? sendToast.description
                : sendToast.status === "error"
                  ? sendToast.description
                : "Tap to see your transaction"}
            </span>
          </span>
        </a>
      )}

      {effectiveFooter &&
        view !== "portfolio" &&
        view !== "send" &&
        view !== "token-detail" && (
        <div className="wi-sticky-footer">
          <div className="wi-footer" ref={footerRef}>
            {effectiveFooter}
          </div>
        </div>
      )}
    </>
  );

  const contentStyle = {
    ...portalStyle,
    ...(footerHeight > 0
      ? ({ "--wi-footer-height": `${footerHeight}px` } as React.CSSProperties)
      : null),
  };

  if (isMobile) {
    return (
      <TrackingBoundary feature="wallet_info">
        <Drawer.Portal>
          {modal && <Drawer.Overlay className="wi-drawer-overlay" />}
          <Drawer.Content
            className={cn("wi-drawer-content", className)}
            data-theme={theme}
            data-vaul-no-drag={
              drawerDirection === "left" || drawerDirection === "right"
                ? true
                : undefined
            }
            style={contentStyle}
            data-direction={drawerDirection}
            data-no-balance={!showBalance || undefined}
            data-has-footer={footerHeight > 0 || undefined}
            data-view={view}
          >
            <Drawer.Title className="wi-sr-only">My Wallet</Drawer.Title>
            {contentBody}
          </Drawer.Content>
        </Drawer.Portal>
      </TrackingBoundary>
    );
  }

  return (
    <TrackingBoundary feature="wallet_info">
      {modal && open && <div className="wi-drawer-overlay" onClick={close} />}
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={sideOffset}
          className={cn("wi-popover-content", className)}
          data-theme={theme}
          style={contentStyle}
          data-no-balance={!showBalance || undefined}
          data-has-footer={footerHeight > 0 || undefined}
          data-view={view}
        >
          {contentBody}
        </Popover.Content>
      </Popover.Portal>
    </TrackingBoundary>
  );
}
